import { ConfiguratorType } from "~/Configurators/ConfiguratorType";
import { type RootState } from "~/redux/store";
import { INCH_SHAFT_CONFIGURATOR_PRODUCT_CODE } from "~/Configurators/constants";
import { sendShaftUpdate } from "~/Configurators/Shaft/api/sendShaftConfiguratorUpdate";
import { sendShaftDataReset } from "~/Configurators/Shaft/api/sendShaftStepReset";
import { sendShaftResetConfigurator } from "~/Configurators/Shaft/api/sendShaftResetConfigurator";
import { sendShaftMiscChange, sendShaftStepChange } from "~/Configurators/Shaft/api/sendShaftStepChange";
import { sendShaftAddToCart } from "~/Configurators/Shaft/api/sendShaftAddToCart";
import { type ConfiguratorPageDefinition, type StepHardcodedData } from "~/Configurators/ConfiguratorPageDefinition";
import { SubstepType } from "~/Configurators/Linear/dtos";
import { InchShaftSummaryData } from "~/Configurators/Shaft/inch/components/InchShaftSummaryData";
import {
  AdditionalDataKeys,
  type Kmat,
  type LinearConfiguration,
  type LinearConfigurationUpdate,
} from "~/Configurators/Linear/models";
import * as InchCstics from "~/Configurators/Shaft/inch/inchCsticConstants";
import {
  chamfer,
  radialThread,
  SHAFT_LENGTH_INCH_MAX,
  SHAFT_LENGTH_METRIC_MAX,
} from "~/Configurators/Shaft/inch/inchCsticConstants";
import {
  allCsticsHaveValue,
  allCsticsHaveValueInConfiguration,
  getMainKmat,
  hasVisitedStep,
} from "~/Configurators/Shaft/common/commonShaftHelpers";
import { KmatType } from "~/Configurators/Linear/getKmatType";
import {
  isRadialFullyConfiguredInch,
  showChamferedSection,
  showRadialThreadSection,
  validateChamferSegment,
} from "~/Configurators/Shaft/shaftConfiguratorHelper";
import {
  convertToSnakeCase,
  makeSendStepCompletedEvent,
  makeSummaryDataProvider,
} from "~/Configurators/Shaft/shaftGaHelper";
import { getStep1SummaryData } from "~/Configurators/Shaft/metric/MetricSummaryDataProvider";
import { toMapFn } from "~/utils/reducers";
import { getStep2SummaryData } from "~/Configurators/Shaft/inch/InchSummaryDataProvider";
import type { SummaryData } from "~/Configurators/Shaft/SummaryDataProvider";
import { removeMultipleTags } from "~/utils/domUtils";

function prepareSummaryDataName(summaryData: SummaryData) {
  let name = summaryData.name?.toString() ?? "";
  name = removeMultipleTags(name, "p", "strong")!;
  return convertToSnakeCase(name);
}

const HARDCODED_STEP_DATA: StepHardcodedData[] = [
  {
    resetLabelKey: "",
    resetRequiresConfirmation: true,
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const summaryDataProvider = makeSummaryDataProvider(state);
      const step1Data = getStep1SummaryData(summaryDataProvider) ?? [];

      const selectionData = toMapFn(
        step1Data,
        (data) => convertToSnakeCase(data.name?.toString() ?? ""),
        (data) => data.value?.toString() ?? "",
      );

      sendStepCompletedEvent(summaryDataProvider.getGaStepName(1), selectionData);
    },
    gaName: "",
  },
  {
    resetLabelKey: "",
    resetRequiresConfirmation: true,
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const summaryDataProvider = makeSummaryDataProvider(state);
      const step2data = getStep2SummaryData(summaryDataProvider) ?? [];
      const selectionData: Record<string, string> = {};

      [
        { prefix: "", summary: step2data.length },
        { prefix: "", summary: step2data.tolerance },
        { prefix: step2data.left?.name, summary: step2data.left?.chamfer?.width },
        { prefix: step2data.left?.name, summary: step2data.left?.chamfer?.angle },
        { prefix: step2data.middle?.name, summary: step2data.middle?.radialThread?.aL },
        { prefix: step2data.middle?.name, summary: step2data.middle?.radialThread?.aR },
        { prefix: step2data.middle?.name, summary: step2data.middle?.radialThread?.pdt },
        { prefix: step2data.middle?.name, summary: step2data.middle?.radialThread?.threadDepth },
        { prefix: step2data.middle?.name, summary: step2data.middle?.radialThread?.threadHoleAmount },
        { prefix: step2data.middle?.name, summary: step2data.middle?.radialThread?.threadSpacing },
        { prefix: step2data.right?.name, summary: step2data.right?.chamfer?.width },
        { prefix: step2data.right?.name, summary: step2data.right?.chamfer?.angle },
      ]
        .filter(({ summary }) => summary)
        .forEach(({ prefix, summary }) => {
          let key = [prefix, summary!.name].filter(Boolean).join(" ");
          key = removeMultipleTags(key, "p", "strong")!;
          key = convertToSnakeCase(key);
          selectionData[key] = summary!.value;
        });

      sendStepCompletedEvent(summaryDataProvider.getGaStepName(2), selectionData);
    },
    gaName: "",
  },
];
export const IMPERIAL_SHAFT_DEFINITION: ConfiguratorPageDefinition = {
  type: ConfiguratorType.InchShaft,
  productCode: INCH_SHAFT_CONFIGURATOR_PRODUCT_CODE,
  translationKeys: {
    pageTitle: "configurator.shaft.title.inch",
  },
  modelPreprocessor: (model) => {
    return model;
  },
  apiCalls: {
    update: sendShaftUpdate,
    reset: sendShaftResetConfigurator,
    stepChange: sendShaftStepChange,
    stepReset: sendShaftDataReset,
    miscChange: sendShaftMiscChange,
    addToCart: sendShaftAddToCart,
  },
  summary: InchShaftSummaryData,
  summaryParams: () => <div />,
  isAddToCartEnabled: (state: RootState) => {
    const visitedStep2 = hasVisitedStep(2)(state);
    const hasAllCstics = allCsticsHaveValue(
      KmatType.INCH_SHAFT,
      InchCstics.SHAFT_LENGTH_METRIC,
      InchCstics.SHAFT_LENGTH_INCH,
      InchCstics.shaftTolerance.value,
      InchCstics.SHAFT_MACHINING_LEFT,
      InchCstics.SHAFT_MACHINING_MIDDLE,
      InchCstics.SHAFT_MACHINING_RIGHT,
    )(state);

    return visitedStep2 && hasAllCstics;
  },
  isSaveEnabled: (state: RootState) => {
    const visitedStep2 = hasVisitedStep(2)(state);

    const hasLength = allCsticsHaveValue(
      KmatType.INCH_SHAFT,
      InchCstics.SHAFT_LENGTH_METRIC,
      InchCstics.SHAFT_LENGTH_INCH,
    )(state);

    return visitedStep2 && hasLength;
  },
  stepHardcodedData: HARDCODED_STEP_DATA,
  isConflictAvoidable: (csticName) => {
    const avoidableCstics = [
      InchCstics.SHAFT_LENGTH_METRIC,
      InchCstics.SHAFT_LENGTH_INCH,
      InchCstics.SHAFT_MACHINING_LEFT,
      InchCstics.SHAFT_MACHINING_MIDDLE,
      InchCstics.SHAFT_MACHINING_RIGHT,
      InchCstics.chamfer.left.width.sf,
      InchCstics.chamfer.right.width.sf,
      SHAFT_LENGTH_INCH_MAX,
      SHAFT_LENGTH_METRIC_MAX,
    ];
    return avoidableCstics.includes(csticName);
  },
  generateDesignation: (kmats: Dict<Kmat[]>) => {
    const kmat = kmats[KmatType.INCH_SHAFT]?.[0];

    // Create the designation using `{series}{diameter}-{material}-{left}/{middle}/{right}`;
    // Exception - skip material if it's standard
    const materialCstic = kmat.cstics[InchCstics.MATERIAL];
    const series = kmat.cstics[InchCstics.SERIES]?.value;
    const diameter = kmat.cstics[InchCstics.DIAMETER]?.value;
    const material = materialCstic?.value;
    const left = kmat.cstics[InchCstics.SHAFT_MACHINING_LEFT]?.value;
    const middle = kmat.cstics[InchCstics.SHAFT_MACHINING_MIDDLE]?.value;
    const right = kmat.cstics[InchCstics.SHAFT_MACHINING_RIGHT]?.value;

    const isStandardMaterial = materialCstic?.values.find((v) => v.value === material)?.standard;

    let designation = "";
    const STEPS = [
      [
        () => !!series && !!diameter,
        (currentDesignation: string) => currentDesignation + series! + diameter!.split(".")[0],
      ],
      [
        () => !!material,
        (currentDesignation: string) => currentDesignation + (isStandardMaterial ? "" : "-" + material!),
      ],
      [
        () => !!left || !!middle || !!right,
        (currentDesignation: string) => currentDesignation + "-" + [left, middle, right].filter(Boolean).join("/"),
      ],
    ] as const;

    for (let i = 0; i < STEPS.length; i += 1) {
      if (STEPS[i][0]()) {
        designation = STEPS[i][1](designation);
      } else {
        break; // All following steps are also not valid
      }
    }
    return designation || "N/A";
  },
  postDefinitionProcessor: (model) => {
    const kmat = getMainKmat({
      kmats: model.kmats,
    });

    model.substeps[SubstepType.ShaftMachining]?.groupedTiles?.forEach((group) => {
      group.tiles.forEach((tile) => {
        delete tile.csticsToEnable[chamfer.left.angle];
        delete tile.csticsToEnable[chamfer.right.angle];
        delete tile.csticsToEnable[radialThread.aLaREnabler];
      });
    });
    const leftDeburred = model.substeps[SubstepType.ShaftMachining]?.groupedTiles?.[0]?.tiles.find(
      (tile) => tile.cstics[InchCstics.SHAFT_MACHINING_LEFT] === "005",
    );
    const middleRadialThreadedHoles = model.substeps[SubstepType.ShaftMachining]?.groupedTiles?.[1]?.tiles.find(
      (tile) => tile.cstics[InchCstics.SHAFT_MACHINING_MIDDLE] === "980",
    );
    const rightDeburred = model.substeps[SubstepType.ShaftMachining]?.groupedTiles?.[2]?.tiles.find(
      (tile) => tile.cstics[InchCstics.SHAFT_MACHINING_RIGHT] === "005",
    );

    if (leftDeburred) {
      leftDeburred.csticsToSetOnly = leftDeburred.csticsToSetOnly ?? {};
      leftDeburred.csticsToSetOnly[chamfer.left.angle] = "NF30";
    }
    if (middleRadialThreadedHoles) {
      middleRadialThreadedHoles.csticsToSetOnly = middleRadialThreadedHoles.csticsToSetOnly ?? {};
      middleRadialThreadedHoles.csticsToSetOnly[radialThread.aLaREnabler] = "S";
    }
    if (rightDeburred) {
      rightDeburred.csticsToSetOnly = rightDeburred.csticsToSetOnly ?? {};
      rightDeburred.csticsToSetOnly[chamfer.right.angle] = "NF30";
    }

    const substep = model.substeps[SubstepType.Diameter];
    if (substep && substep.additionalData) {
      substep.additionalData[AdditionalDataKeys.TitleFontSize] = "24px";
    }

    const lengthMetric = kmat.cstics[InchCstics.SHAFT_LENGTH_METRIC];
    const lengthInch = kmat.cstics[InchCstics.SHAFT_LENGTH_INCH];
    [lengthMetric, lengthInch].forEach((cstic) => {
      if (cstic && cstic.value) {
        cstic.value = (+cstic.value).toFixed(4);
      }
    });
    if (lengthMetric) {
      lengthMetric.label = "configurator.shaft.step.2.substep.1.title.metric";
    }
    if (lengthInch) {
      lengthInch.label = "configurator.shaft.step.2.substep.1.title.inch";
    }

    const page2 = model.steps[1];
    if (page2 && "defaultValues" in page2) {
      const defaultValues = page2.defaultValues as Record<string, string | null>;
      // Shaft Length
      defaultValues[InchCstics.SHAFT_LENGTH_METRIC] = null;
      defaultValues[InchCstics.SHAFT_LENGTH_INCH] = null;
      // Shaft Machining
      defaultValues[InchCstics.SHAFT_MACHINING_LEFT] = null;
      defaultValues[InchCstics.SHAFT_MACHINING_MIDDLE] = null;
      defaultValues[InchCstics.SHAFT_MACHINING_RIGHT] = null;
      // Machining extra fierlds
      defaultValues[chamfer.left.angle] = null;
      defaultValues[chamfer.left.width.sf] = null;
      defaultValues[chamfer.right.angle] = null;
      defaultValues[chamfer.right.width.sf] = null;
      defaultValues[radialThread.aL.inch.value] = null;
      defaultValues[radialThread.aR.inch.value] = null;
      defaultValues[radialThread.aL.metric.value] = null;
      defaultValues[radialThread.aR.metric.value] = null;
      defaultValues[radialThread.aLaREnabler] = null;
    }

    model.allowedSteps = calculateAllowedSteps(model);

    // SCHAEFF-31580 - Manually validate that the length is not too much, and create a conflict if it exceeds max
    [
      [InchCstics.SHAFT_LENGTH_METRIC, InchCstics.SHAFT_LENGTH_METRIC_MAX],
      [InchCstics.SHAFT_LENGTH_INCH, InchCstics.SHAFT_LENGTH_INCH_MAX],
    ].forEach(([lengthCsticName, maxLengthCsticName]) => {
      const lengthValue = kmat.cstics[lengthCsticName]?.value;
      const maxLengthValue = kmat.cstics[maxLengthCsticName]?.value;
      if (lengthValue && maxLengthValue) {
        const length = +lengthValue;
        const maxLength = +maxLengthValue;
        if (length > maxLength && "conflictedCstics" in model) {
          model.conflictedCstics.push({
            id: "Invalid length",
            name: "Invalid length",
            cstics: [
              {
                name: lengthCsticName,
                key: lengthCsticName,
              },
              {
                name: maxLengthCsticName,
                key: maxLengthCsticName,
              },
            ],
          });
        }
      }
    });

    return model;
  },
  gaConfiguratorName: "shaft-inch",
  getGaStepName: (step) => HARDCODED_STEP_DATA[step]?.gaName,
} as const;

function calculateAllowedSteps(model: LinearConfiguration | LinearConfigurationUpdate): boolean[][] {
  const allowedSteps = [
    [true, false, false],
    [true, true, false],
    [true, true, true],
  ];

  const canVisitStep2 = allCsticsHaveValueInConfiguration(
    KmatType.INCH_SHAFT,
    InchCstics.SERIES,
    InchCstics.DIAMETER,
    InchCstics.MATERIAL,
    InchCstics.COATING,
    InchCstics.TOLERANCE,
  )(model);
  allowedSteps[0][1] = canVisitStep2;

  let canVisitStep3 = allCsticsHaveValueInConfiguration(
    KmatType.INCH_SHAFT,
    InchCstics.SHAFT_LENGTH_METRIC,
    InchCstics.SHAFT_LENGTH_INCH,
    InchCstics.shaftTolerance.value,
    InchCstics.SHAFT_MACHINING_LEFT,
    InchCstics.SHAFT_MACHINING_MIDDLE,
    InchCstics.SHAFT_MACHINING_RIGHT,
  )(model);

  const kmat = getMainKmat({ kmats: model.kmats });
  const leftMachining = kmat.cstics[InchCstics.SHAFT_MACHINING_LEFT]?.value;
  const middleMachining = kmat.cstics[InchCstics.SHAFT_MACHINING_MIDDLE]?.value;
  const rightMachining = kmat.cstics[InchCstics.SHAFT_MACHINING_RIGHT]?.value;

  let conflictedCstics: string[];
  if ("conflictedCstics" in model) {
    conflictedCstics = model.conflictedCstics.flatMap((c) => c.cstics.map((c) => c.name));
  } else {
    conflictedCstics = [];
  }

  const validationCtx = { kmat, conflictedCstics, mode: "full" } as const;

  if (showChamferedSection(leftMachining)) {
    canVisitStep3 &&= validateChamferSegment(InchCstics.chamfer.left, validationCtx).length === 0;
  }
  if (showChamferedSection(rightMachining)) {
    canVisitStep3 &&= validateChamferSegment(InchCstics.chamfer.right, validationCtx).length === 0;
  }
  if (showRadialThreadSection(middleMachining)) {
    canVisitStep3 &&= isRadialFullyConfiguredInch(kmat);
  }
  allowedSteps[1][2] = canVisitStep3;

  return allowedSteps;
}

const sendStepCompletedEvent = makeSendStepCompletedEvent(IMPERIAL_SHAFT_DEFINITION);
